import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ChevronDown, ChevronRight, Sparkles, Plus, Trash2, BookOpen, Loader2,
  ListChecks, Save, AlertCircle, Download, FileArchive,
} from "lucide-react";

/**
 * Quiz Library — admin landing page that shows every course → every module
 * with its quiz, fully editable. Built so course authors can fine-tune
 * AI-generated questions without leaving the admin.
 */
export default function QuizLibrary() {
  const [courses, setCourses] = useState([]);
  const [modulesByCourse, setModulesByCourse] = useState({});
  const [expanded, setExpanded] = useState({}); // moduleId -> bool
  const [quizzes, setQuizzes] = useState({}); // moduleId -> {quiz, dirty, saving}
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({}); // moduleId -> bool
  const [downloading, setDownloading] = useState(false);
  const [downloadDialog, setDownloadDialog] = useState(null); // { url, filename, size }

  // Generic blob downloader. We fetch with native fetch() so we can attach
  // the Authorization header AND keep full control of the response stream.
  // The dialog is the PRIMARY save mechanism — a visible <a download> click
  // is a trusted user gesture every browser honors (Safari, Chrome, Firefox).
  // We deliberately do NOT auto-click in JS — those clicks are silently
  // blocked after an async fetch, which gives a false "downloaded" feeling.
  const fetchAndShowDialog = async ({ path, defaultName, mime, loadingLabel, fileKind }) => {
    setDownloading(true);
    const t = toast.loading(loadingLabel);
    try {
      const token = localStorage.getItem("uvc_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}${path}`;
      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status} ${res.statusText}`);
      }

      const dispo = res.headers.get("content-disposition") || "";
      const match = /filename="?([^"]+)"?/.exec(dispo);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      const filename = match ? match[1] : `${defaultName}-${stamp}`;

      // Force the correct MIME so the OS knows what kind of file this is.
      const rawBlob = await res.blob();
      const blob = new Blob([rawBlob], { type: mime });
      const objUrl = URL.createObjectURL(blob);

      toast.dismiss(t);
      setDownloadDialog({ url: objUrl, filename, size: blob.size, kind: fileKind });
    } catch (e) {
      toast.dismiss(t);
      const msg = e?.message || "Unknown error";
      toast.error("Download failed", { description: msg.slice(0, 200) });
      console.error(`[QuizLibrary] ${fileKind} download failed:`, e);
    } finally { setDownloading(false); }
  };

  const downloadDocx = () => fetchAndShowDialog({
    path: "/api/quizzes/export",
    defaultName: "univicoustic-quizzes",
    mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    loadingLabel: "Building Word file…",
    fileKind: "Microsoft Word",
  });

  const downloadPdfZip = () => fetchAndShowDialog({
    path: "/api/quizzes/export-pdf-zip",
    defaultName: "univicoustic-quizzes.zip",
    mime: "application/zip",
    loadingLabel: "Building PDF bundle…",
    fileKind: "ZIP of PDFs",
  });

  const closeDialog = () => {
    if (downloadDialog?.url) {
      // Revoke after a delay so any in-progress download completes first
      const u = downloadDialog.url;
      setTimeout(() => URL.revokeObjectURL(u), 60_000);
    }
    setDownloadDialog(null);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const { data: cs } = await api.get("/courses");
      setCourses(cs || []);
      const byCourse = {};
      await Promise.all(
        (cs || []).map(async (c) => {
          const { data: mods } = await api.get(`/modules?course_id=${c.id}`);
          byCourse[c.id] = mods || [];
        })
      );
      setModulesByCourse(byCourse);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const ensureQuizLoaded = async (mod) => {
    if (quizzes[mod.id]) return;
    try {
      const { data } = await api.get(`/modules/${mod.id}/quiz`);
      setQuizzes((q) => ({ ...q, [mod.id]: { quiz: data.quiz || [], dirty: false, saving: false } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load quiz");
    }
  };

  const toggle = (mod) => {
    setExpanded((e) => ({ ...e, [mod.id]: !e[mod.id] }));
    if (!expanded[mod.id]) ensureQuizLoaded(mod);
  };

  const update = (modId, idx, patch) => {
    setQuizzes((qs) => {
      const cur = qs[modId];
      if (!cur) return qs;
      const quiz = cur.quiz.map((q, i) => (i === idx ? { ...q, ...patch } : q));
      return { ...qs, [modId]: { ...cur, quiz, dirty: true } };
    });
  };

  const updateOption = (modId, qIdx, optIdx, value) => {
    setQuizzes((qs) => {
      const cur = qs[modId];
      if (!cur) return qs;
      const quiz = cur.quiz.map((q, i) => {
        if (i !== qIdx) return q;
        const options = q.options.map((o, j) => (j === optIdx ? value : o));
        return { ...q, options };
      });
      return { ...qs, [modId]: { ...cur, quiz, dirty: true } };
    });
  };

  const addQuestion = (modId) => {
    setQuizzes((qs) => {
      const cur = qs[modId] || { quiz: [], dirty: true, saving: false };
      const quiz = [
        ...cur.quiz,
        { q: "New question — edit this", options: ["Option A", "Option B", "Option C", "Option D"], correct_idx: 0, explanation: "" },
      ];
      return { ...qs, [modId]: { ...cur, quiz, dirty: true } };
    });
  };

  const removeQuestion = (modId, idx) => {
    setQuizzes((qs) => {
      const cur = qs[modId];
      if (!cur) return qs;
      const quiz = cur.quiz.filter((_, i) => i !== idx);
      return { ...qs, [modId]: { ...cur, quiz, dirty: true } };
    });
  };

  const saveQuiz = async (modId) => {
    const cur = quizzes[modId];
    if (!cur) return;
    setQuizzes((qs) => ({ ...qs, [modId]: { ...cur, saving: true } }));
    try {
      // Strip empty options
      const cleaned = cur.quiz.map((q) => ({
        q: (q.q || "").trim(),
        options: (q.options || []).map((o) => (o || "").trim()),
        correct_idx: Math.max(0, Math.min((q.correct_idx ?? 0), (q.options?.length || 1) - 1)),
        explanation: (q.explanation || "").trim(),
      }));
      // Light validation
      for (const q of cleaned) {
        if (!q.q) return toast.error("Every question needs text");
        if (q.options.length < 2 || q.options.some((o) => !o)) return toast.error("Each question needs at least 2 non-empty options");
      }
      await api.patch(`/modules/${modId}`, { quiz: cleaned });
      setQuizzes((qs) => ({ ...qs, [modId]: { quiz: cleaned, dirty: false, saving: false } }));
      toast.success("Quiz saved");
    } catch (e) {
      setQuizzes((qs) => ({ ...qs, [modId]: { ...cur, saving: false } }));
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const regenerate = async (modId, difficulty = "medium") => {
    setGenerating((g) => ({ ...g, [modId]: true }));
    try {
      const { data } = await api.post(`/modules/${modId}/generate-quiz?difficulty=${difficulty}`);
      setQuizzes((qs) => ({ ...qs, [modId]: { quiz: data.quiz || [], dirty: false, saving: false } }));
      toast.success(`Regenerated ${data.count || 0} questions`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating((g) => ({ ...g, [modId]: false }));
    }
  };

  const totalQuestions = useMemo(() => {
    let n = 0;
    Object.values(modulesByCourse).forEach((mods) => mods.forEach((m) => { n += (m.quiz?.length || 0); }));
    return n;
  }, [modulesByCourse]);

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-8 py-10">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-slate-400 font-semibold mb-1.5">Knowledge checks</div>
            <h1 className="font-display text-3xl text-slate-900 mb-1.5" data-testid="quiz-library-title">Quiz Library</h1>
            <p className="text-sm text-slate-500">Edit every AI-generated question across all your courses. Click a module to expand.</p>
          </div>
          <div className="text-xs text-slate-500 text-right flex items-center gap-3">
            <span>{courses.length} courses · {totalQuestions} questions</span>
            <Button
              onClick={downloadPdfZip}
              disabled={downloading || loading}
              size="sm"
              className="bg-[#E53935] hover:bg-[#c62828] text-white gap-1.5 shadow-sm"
              data-testid="quiz-download-pdf-zip"
              title="Download every quiz as a ZIP of PDF files (one PDF per module)"
            >
              {downloading ? <Loader2 size={13} className="animate-spin"/> : <FileArchive size={13}/>}
              Download PDFs (.zip)
            </Button>
            <Button
              onClick={downloadDocx}
              disabled={downloading || loading}
              size="sm"
              variant="outline"
              className="gap-1.5"
              data-testid="quiz-download-docx"
              title="Download every quiz across all modules as a single Word file"
            >
              {downloading ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>}
              Download .docx
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Loading…</div>
        ) : courses.length === 0 ? (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">
            No courses yet. Create one in <strong>Courses</strong> first.
          </div>
        ) : courses.map((c) => (
          <section key={c.id} className="mb-10" data-testid={`quiz-course-${c.id}`}>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
              <BookOpen size={15} className="text-slate-400"/>
              <h2 className="text-base font-display font-semibold text-slate-900">{c.title}</h2>
              {!c.published && <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Draft</span>}
            </div>
            <div className="space-y-2">
              {(modulesByCourse[c.id] || []).map((m) => {
                const q = quizzes[m.id];
                const isExpanded = !!expanded[m.id];
                return (
                  <div key={m.id} className="border border-slate-200 rounded-lg bg-white" data-testid={`quiz-module-${m.id}`}>
                    <button
                      onClick={() => toggle(m)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                      data-testid={`quiz-module-toggle-${m.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {isExpanded ? <ChevronDown size={15} className="text-slate-400 shrink-0"/> : <ChevronRight size={15} className="text-slate-400 shrink-0"/>}
                        <span className="text-sm font-medium text-slate-900 truncate">{m.title}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 inline-flex items-center gap-1">
                          <ListChecks size={11}/> {(m.quiz?.length ?? q?.quiz?.length ?? 0)} questions
                        </span>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider">{m.quiz_difficulty || "medium"}</span>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-4">
                        {!q ? (
                          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={13}/> Loading…</div>
                        ) : (
                          <ModuleQuizEditor
                            mod={m}
                            quiz={q.quiz}
                            dirty={q.dirty}
                            saving={q.saving}
                            generating={!!generating[m.id]}
                            onUpdate={(idx, patch) => update(m.id, idx, patch)}
                            onUpdateOption={(qIdx, oIdx, val) => updateOption(m.id, qIdx, oIdx, val)}
                            onAdd={() => addQuestion(m.id)}
                            onRemove={(idx) => removeQuestion(m.id, idx)}
                            onSave={() => saveQuiz(m.id)}
                            onRegenerate={(d) => regenerate(m.id, d)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {(modulesByCourse[c.id] || []).length === 0 && (
                <div className="text-xs text-slate-400 italic px-4 py-3">No modules yet in this course.</div>
              )}
            </div>
          </section>
        ))}
      </div>

      {/* Download dialog — the visible <a download> link is the trusted user gesture
          that triggers the actual file save across all browsers. */}
      {downloadDialog && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center px-4"
          onClick={closeDialog}
          data-testid="quiz-download-dialog"
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] uppercase tracking-[3px] text-[#E53935] font-bold mb-1.5">Quiz Library</div>
            <h3 className="font-display text-xl text-slate-900 mb-2">
              Your {downloadDialog.kind === "ZIP of PDFs" ? "PDF bundle" : "Word file"} is ready
            </h3>
            <p className="text-sm text-slate-500 mb-5">
              Click the red button below to save the file to your computer.
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3 mb-5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-mono text-slate-900 truncate">{downloadDialog.filename}</div>
                <div className="text-[11px] text-slate-500">{(downloadDialog.size / 1024).toFixed(0)} KB · {downloadDialog.kind || "File"}</div>
              </div>
            </div>
            <a
              href={downloadDialog.url}
              download={downloadDialog.filename}
              onClick={() => {
                toast.success("Saved to your Downloads folder");
                setTimeout(closeDialog, 800);
              }}
              className="block w-full text-center py-3 rounded-md bg-[#E53935] hover:bg-[#c62828] text-white font-semibold transition-colors"
              data-testid="quiz-download-link"
            >
              ↓ Save {downloadDialog.filename}
            </a>
            <a
              href={downloadDialog.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center py-2 mt-2 text-xs text-slate-500 hover:text-[#E53935] underline"
              data-testid="quiz-download-newtab"
            >
              Or open in a new tab (then use ⌘/Ctrl + S)
            </a>
            <p className="text-[11px] text-slate-400 text-center mt-3">
              Tip: if your browser blocks the download, right-click the red button → “Save link as…”
            </p>
            <button
              onClick={closeDialog}
              className="block w-full text-center py-2 mt-2 text-xs text-slate-500 hover:text-slate-900"
              data-testid="quiz-download-dialog-close"
            >Close</button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function ModuleQuizEditor({ mod, quiz, dirty, saving, generating, onUpdate, onUpdateOption, onAdd, onRemove, onSave, onRegenerate }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500">{quiz.length} question{quiz.length !== 1 ? "s" : ""}{dirty && <span className="ml-2 text-amber-600 font-medium">• Unsaved changes</span>}</div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => onRegenerate(mod.quiz_difficulty || "medium")} disabled={generating} data-testid={`quiz-regen-${mod.id}`} className="h-8 text-xs gap-1.5">
            {generating ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>} Regenerate with AI
          </Button>
          <Button size="sm" onClick={onSave} disabled={!dirty || saving} className="h-8 text-xs gap-1.5 bg-[#E53935] hover:bg-[#c62828] text-white" data-testid={`quiz-save-${mod.id}`}>
            {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save quiz
          </Button>
        </div>
      </div>

      {quiz.length === 0 ? (
        <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-md p-6 text-center bg-white">
          <AlertCircle className="mx-auto mb-2 text-slate-300" size={20}/>
          No quiz yet. Use <strong>Regenerate with AI</strong> or click <strong>+ Add question</strong> below.
        </div>
      ) : (
        <div className="space-y-3">
          {quiz.map((q, idx) => (
            <div key={idx} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`quiz-q-${mod.id}-${idx}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Question {idx + 1}</span>
                <button
                  onClick={() => onRemove(idx)}
                  className="text-slate-400 hover:text-red-600 p-1"
                  title="Delete this question"
                  data-testid={`quiz-q-delete-${mod.id}-${idx}`}
                ><Trash2 size={13}/></button>
              </div>
              <Textarea
                rows={2}
                value={q.q || ""}
                onChange={(e) => onUpdate(idx, { q: e.target.value })}
                placeholder="Question text"
                className="mb-3 text-sm"
                data-testid={`quiz-q-text-${mod.id}-${idx}`}
              />
              <div className="space-y-1.5 mb-3">
                {(q.options || []).map((opt, oIdx) => (
                  <div key={oIdx} className="flex items-center gap-2">
                    <button
                      onClick={() => onUpdate(idx, { correct_idx: oIdx })}
                      className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                        q.correct_idx === oIdx
                          ? "bg-emerald-500 border-emerald-500"
                          : "border-slate-300 hover:border-emerald-500"
                      }`}
                      title={q.correct_idx === oIdx ? "Correct answer" : "Mark as correct answer"}
                      data-testid={`quiz-q-correct-${mod.id}-${idx}-${oIdx}`}
                    >
                      {q.correct_idx === oIdx && <span className="block w-2 h-2 bg-white rounded-full"/>}
                    </button>
                    <Input
                      value={opt}
                      onChange={(e) => onUpdateOption(idx, oIdx, e.target.value)}
                      placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                      className="h-8 text-sm"
                      data-testid={`quiz-q-option-${mod.id}-${idx}-${oIdx}`}
                    />
                    {q.options.length > 2 && (
                      <button
                        onClick={() => {
                          const newOptions = q.options.filter((_, j) => j !== oIdx);
                          const newCorrect = q.correct_idx === oIdx ? 0 : (q.correct_idx > oIdx ? q.correct_idx - 1 : q.correct_idx);
                          onUpdate(idx, { options: newOptions, correct_idx: newCorrect });
                        }}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Remove this option"
                        data-testid={`quiz-q-option-remove-${mod.id}-${idx}-${oIdx}`}
                      ><Trash2 size={12}/></button>
                    )}
                  </div>
                ))}
                {q.options.length < 6 && (
                  <button
                    onClick={() => onUpdate(idx, { options: [...q.options, ""] })}
                    className="text-xs text-blue-700 hover:underline ml-7"
                    data-testid={`quiz-q-add-option-${mod.id}-${idx}`}
                  >+ Add another option</button>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Explanation (shown to learner after they answer)</div>
              <Textarea
                rows={2}
                value={q.explanation || ""}
                onChange={(e) => onUpdate(idx, { explanation: e.target.value })}
                placeholder="Why is the marked answer correct?"
                className="text-xs"
                data-testid={`quiz-q-explanation-${mod.id}-${idx}`}
              />
            </div>
          ))}
        </div>
      )}

      <Button
        size="sm"
        variant="outline"
        onClick={onAdd}
        className="mt-3 h-8 text-xs gap-1.5"
        data-testid={`quiz-add-${mod.id}`}
      ><Plus size={12}/> Add question</Button>
    </div>
  );
}
